import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { VioTablesV2Component } from 'vio-tables-v2';
import type { VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { ApiRequestsService } from '../../services/api-requests.service';

declare const bootstrap: any;

/**
 * Role Master, BU Master and Department Master are all "a name and a list" —
 * this one component drives all three, configured per-route via route data.
 */
@Component({
  selector: 'app-simple-master',
  standalone: true,
  imports: [CommonModule, FormsModule, VioTablesV2Component],
  templateUrl: './simple-master.component.html',
  styleUrl: './simple-master.component.scss',
})
export class SimpleMasterComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('addMasterModal') addMasterModalRef!: ElementRef<HTMLElement>;
  @ViewChild('empListModal') empListModalRef?: ElementRef<HTMLElement>;

  title = '';
  subheading = '';
  fieldLabel = 'Name';
  addButtonLabel = 'Add';
  icon = 'pe-7s-note2';
  private apiBase = '';

  records: any[] = [];
  isLoading = false;
  errorMessage = '';

  newName = '';
  isSaving = false;

  // Employee list popup state
  employeeField = '';
  allEmployees: any[] = [];
  filteredEmployees: any[] = [];
  empListTitle = '';

  tableConfig: VT2_TABLE_CONFIG = {
    tableTitle: '',
    excelFileName: 'master',
    columnLabels: {
      name: 'Name',
      createdAt: 'Added On',
    },
    excludeFields: ['_id'],
    dateFields: ['createdAt'],
    isSortable: true,
    showFormulaButton: false,
    showSummationButton: false,
    showHeaderGroupButton: false,
  };

  empListTableConfig: VT2_TABLE_CONFIG = {
    tableTitle: '',
    excelFileName: 'employees',
    columnLabels: {
      name: 'Name',
      empNo: 'Employee No.',
      department: 'Department',
      role: 'Role',
      bu: 'BU',
      whatsappNumber: 'WhatsApp',
    },
    excludeFields: ['_id', 'createdAt', 'dob', 'esiNo'],
    isSortable: true,
    showFormulaButton: false,
    showSummationButton: false,
    showHeaderGroupButton: false,
  };

  constructor(
    private route: ActivatedRoute,
    private apiService: ApiRequestsService,
  ) {}

  ngOnInit(): void {
    const data = this.route.snapshot.data;
    this.title = data['title'] || '';
    this.subheading = data['subheading'] || '';
    this.fieldLabel = data['fieldLabel'] || 'Name';
    this.addButtonLabel = data['addButtonLabel'] || 'Add';
    this.icon = data['icon'] || this.icon;
    this.apiBase = data['apiBase'] || '';
    this.employeeField = data['employeeField'] || '';

    this.tableConfig = {
      ...this.tableConfig,
      tableTitle: this.title,
      excelFileName: data['excelFileName'] || 'master',
      columnLabels: { name: this.fieldLabel, createdAt: 'Added On' },
    };

    if (this.employeeField) {
      this.tableConfig = {
        ...this.tableConfig,
        actionButtons: [
          {
            icon: '👥',
            label: 'Employees',
            title: 'View employees in this ' + this.title.replace(' Master', '').toLowerCase(),
            onClick: (row) => this.showEmployees(row),
          },
        ],
      };
      this.loadAllEmployees();
    }

    this.loadRecords();
  }

  ngAfterViewInit(): void {
    this.prepareModal(this.addMasterModalRef);
    if (this.empListModalRef) this.prepareModal(this.empListModalRef);
  }

  ngOnDestroy(): void {
    this.detachModal(this.addMasterModalRef);
    if (this.empListModalRef) this.detachModal(this.empListModalRef);
  }

  private prepareModal(modalRef: ElementRef<HTMLElement>): void {
    const modalEl = modalRef?.nativeElement;
    if (!modalEl) return;
    document.body.appendChild(modalEl);
    modalEl.addEventListener('hide.bs.modal', () => {
      if (modalEl.contains(document.activeElement)) {
        (document.activeElement as HTMLElement)?.blur();
      }
    });
  }

  private detachModal(modalRef: ElementRef<HTMLElement>): void {
    const modalEl = modalRef?.nativeElement;
    if (modalEl?.parentNode === document.body) document.body.removeChild(modalEl);
  }

  private openModal(modalRef: ElementRef<HTMLElement>): void {
    const modalEl = modalRef?.nativeElement;
    if (!modalEl) return;
    (bootstrap?.Modal?.getInstance(modalEl) || new bootstrap.Modal(modalEl)).show();
  }

  private closeModal(modalRef: ElementRef<HTMLElement>): void {
    const modalEl = modalRef?.nativeElement;
    if (!modalEl) return;
    (bootstrap?.Modal?.getInstance(modalEl) || new bootstrap.Modal(modalEl)).hide();
  }

  resetForAdd() {
    this.newName = '';
    this.errorMessage = '';
  }

  loadRecords() {
    this.isLoading = true;
    this.errorMessage = '';
    this.apiService.getApiCall(`${this.apiBase}/list`).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res && res.errorCode === this.apiService.NO_ERROR) {
          this.records = res.Data || [];
        } else {
          this.errorMessage = res?.errorDescription || 'Unable to load the list.';
        }
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'Unable to load the list. Please check your connection.';
      },
    });
  }

  private loadAllEmployees(): void {
    this.apiService.getApiCall('api/v1/employeemaster/list').subscribe({
      next: (res: any) => {
        if (res?.errorCode === this.apiService.NO_ERROR) {
          this.allEmployees = res.Data || [];
        }
      },
    });
  }

  showEmployees(row: any): void {
    this.empListTitle = row.name;
    this.filteredEmployees = this.allEmployees.filter(
      e => e[this.employeeField] === row.name,
    );
    if (this.empListModalRef) this.openModal(this.empListModalRef);
  }

  async addRecord() {
    if (!this.newName.trim()) return;
    this.isSaving = true;
    this.errorMessage = '';
    try {
      const res = await this.apiService.postApiCallReturningPromise(`${this.apiBase}/add`, { name: this.newName.trim() });
      if (res && res.errorCode === this.apiService.NO_ERROR) {
        this.newName = '';
        this.closeModal(this.addMasterModalRef);
        this.loadRecords();
      } else {
        this.errorMessage = res?.errorDescription || 'Unable to add this entry.';
      }
    } catch (err) {
      this.errorMessage = 'Unable to add this entry.';
    } finally {
      this.isSaving = false;
    }
  }
}
